import { useEffect, useState } from "react";
import { WifiOff, X } from "lucide-react";
import { Button } from "@/components/ui/button";

const DISMISS_SESSION_KEY = "kudoz:permissions-degraded-dismissed";

/**
 * Small unobtrusive banner shown above protected pages when permission
 * checks are being served from the local cache because the upstream
 * permissions service is briefly unreachable. Auto-disappears the moment
 * the live fetch succeeds (controlled by the parent gating component).
 *
 * The banner is dismissible per browser session — once a user closes it,
 * it stays closed until they open a new tab/window. This keeps the UI
 * out of the way for users actively working through a known outage.
 */
export default function PermissionsDegradedBanner() {
  const [dismissed, setDismissed] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    try {
      return window.sessionStorage.getItem(DISMISS_SESSION_KEY) === "1";
    } catch {
      return false;
    }
  });

  // If the banner was previously dismissed in the same session but the
  // outage stretches across re-mounts (e.g. partner navigates between
  // protected pages), keep it dismissed. We DO NOT auto-resurrect.
  useEffect(() => {
    if (!dismissed) return;
    try {
      window.sessionStorage.setItem(DISMISS_SESSION_KEY, "1");
    } catch {
      // noop
    }
  }, [dismissed]);

  if (dismissed) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="banner-permissions-degraded"
      className="flex items-center gap-2 border-b bg-muted px-4 py-2 text-sm text-muted-foreground"
    >
      <WifiOff className="h-4 w-4 shrink-0" />
      <span className="flex-1">
        Some access checks are using cached data — we'll update automatically
        when service is back.
      </span>
      <Button
        variant="ghost"
        size="icon"
        className="h-7 w-7"
        onClick={() => setDismissed(true)}
        data-testid="button-dismiss-permissions-degraded"
        aria-label="Dismiss"
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
}
